import { queryOptions, useQuery } from '@tanstack/react-query';

import { requireSuccess } from '../../shared/lib/requireSuccess';
import { getInformation, getLineStatus } from './settingsApi';

/** `/users/me` flattened into one object, the user's own fields winning over the company's. */
export interface AccountInfo {
    display_name?: string;
    name?: string;
    email?: string;
    phone_number?: string;
    company_group?: string;
    is_vendor?: boolean;
    features?: string[];
    preference?: { language?: string; temp_unit?: string; entry_page?: string };
}

export interface LineAccount {
    line_user_id: string;
    display_name?: string | null;
    picture_url?: string | null;
}

export const informationKeys = {
    all: ['information'] as const,
};

export const lineKeys = {
    all: ['line'] as const,
    status: () => [...lineKeys.all, 'status'] as const,
};

const LINE_POLL_INTERVAL_MS = 4000;

const toAccountInfo = (data: any): AccountInfo => ({ ...data.company, ...data.user });

/** The raw `/users/me` response, for `fetchQuery` outside a component. */
export const informationQuery = queryOptions({
    queryKey: informationKeys.all,
    queryFn: async () => {
        const data = await getInformation();
        if (!data?.user || !data?.company) {
            throw new Error(data?.message ?? 'getInformation failed');
        }
        return data;
    },
    // Fetched once per session like before; saving settings invalidates it.
    staleTime: Infinity,
});

/** The signed-in account. Pass `enabled: false` on routes that can render without a session. */
export const useInformationQuery = ({ enabled = true }: { enabled?: boolean } = {}) =>
    useQuery({ ...informationQuery, select: toAccountInfo, enabled });

/** Linked LINE accounts; `poll` keeps checking while a bind code is waiting to be used. */
export const useLineStatusQuery = ({ poll = false }: { poll?: boolean } = {}) =>
    useQuery({
        queryKey: lineKeys.status(),
        queryFn: async (): Promise<LineAccount[]> => {
            const data = requireSuccess(await getLineStatus());
            return Array.isArray(data.accounts) ? data.accounts : [];
        },
        refetchInterval: poll ? LINE_POLL_INTERVAL_MS : false,
        // Coming back from the LINE app shows the new account without waiting for the next poll.
        refetchOnWindowFocus: poll,
    });
